/** Procedural, local-only modelling sounds built with Web Audio. */

export type SculptSoundKind = "carve" | "smooth";
export type ActionSoundKind = "cut" | "angle" | "fixing" | "flange" | "overall";

let context: AudioContext | null = null;
let loopSource: AudioBufferSourceNode | null = null;
let noiseBuffer: AudioBuffer | null = null;
let bandpass: BiquadFilterNode | null = null;
let lowpass: BiquadFilterNode | null = null;
let output: GainNode | null = null;
let activeKind: SculptSoundKind = "carve";

function ensureGraph(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (context && loopSource && noiseBuffer && bandpass && lowpass && output) return context;

  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass) return null;

  context = new AudioContextClass();
  const sampleRate = context.sampleRate;
  noiseBuffer = context.createBuffer(1, sampleRate, sampleRate);
  const samples = noiseBuffer.getChannelData(0);
  let previous = 0;
  for (let i = 0; i < samples.length; i++) {
    const white = Math.random() * 2 - 1;
    previous = previous * 0.28 + white * 0.72;
    const grain = 0.7 + 0.3 * Math.sin((i / sampleRate) * Math.PI * 2 * 31);
    samples[i] = previous * grain;
  }

  loopSource = context.createBufferSource();
  loopSource.buffer = noiseBuffer;
  loopSource.loop = true;
  bandpass = context.createBiquadFilter();
  bandpass.type = "bandpass";
  bandpass.Q.value = 0.72;
  lowpass = context.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.Q.value = 0.35;
  output = context.createGain();
  output.gain.value = 0;

  loopSource.connect(bandpass).connect(lowpass).connect(output).connect(context.destination);
  loopSource.start();
  return context;
}

export function startSculptSound(
  kind: SculptSoundKind,
  radius: number,
  strength: number,
): void {
  const audio = ensureGraph();
  if (!audio || !output) return;
  activeKind = kind;
  void audio.resume();
  updateSculptSound(kind, radius, strength);
  const now = audio.currentTime;
  output.gain.cancelScheduledValues(now);
  output.gain.setTargetAtTime(kind === "smooth" ? 0.022 : 0.035, now, 0.025);
}

export function updateSculptSound(
  kind: SculptSoundKind,
  radius: number,
  strength: number,
): void {
  if (!context || !bandpass || !lowpass || !output) return;
  activeKind = kind;
  const now = context.currentTime;
  const radiusMix = Math.min(1, Math.max(0, (radius - 4) / 100));
  const strengthMix = Math.min(1, Math.max(0, strength / (kind === "smooth" ? 0.5 : 0.2)));

  if (kind === "smooth") {
    bandpass.frequency.setTargetAtTime(520 - radiusMix * 170, now, 0.05);
    bandpass.Q.setTargetAtTime(0.42, now, 0.05);
    lowpass.frequency.setTargetAtTime(1250 + strengthMix * 450, now, 0.05);
    output.gain.setTargetAtTime(0.012 + strengthMix * 0.018, now, 0.035);
  } else {
    bandpass.frequency.setTargetAtTime(1450 - radiusMix * 650, now, 0.035);
    bandpass.Q.setTargetAtTime(0.72, now, 0.035);
    lowpass.frequency.setTargetAtTime(2100 + strengthMix * 1000, now, 0.035);
    output.gain.setTargetAtTime(0.018 + strengthMix * 0.024, now, 0.025);
  }
}

export function stopSculptSound(): void {
  if (!context || !output) return;
  const now = context.currentTime;
  output.gain.cancelScheduledValues(now);
  output.gain.setTargetAtTime(0, now, activeKind === "smooth" ? 0.06 : 0.035);
}

/** Short feedback for discrete modelling actions. */
export function playActionSound(kind: ActionSoundKind): void {
  const audio = ensureGraph();
  if (!audio || !noiseBuffer) return;
  void audio.resume();
  const now = audio.currentTime;

  const burst = audio.createBufferSource();
  burst.buffer = noiseBuffer;
  const filter = audio.createBiquadFilter();
  filter.type = kind === "fixing" ? "highpass" : "bandpass";
  filter.frequency.value = kind === "cut" ? 920 : kind === "angle" ? 1800 : kind === "flange" ? 480 : kind === "overall" ? 680 : 1200;
  filter.Q.value = kind === "cut" ? 0.55 : 1.1;
  const gain = audio.createGain();
  const duration = kind === "cut" ? 0.24 : kind === "angle" ? 0.055 : kind === "flange" ? 0.2 : kind === "overall" ? 0.3 : 0.11;
  const peak = kind === "cut" ? 0.075 : kind === "angle" ? 0.025 : kind === "flange" ? 0.04 : kind === "overall" ? 0.035 : 0.045;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(peak, now + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  burst.connect(filter).connect(gain).connect(audio.destination);
  burst.start(now);
  burst.stop(now + duration + 0.02);

  if (kind !== "cut" && kind !== "flange" && kind !== "overall") {
    const tone = audio.createOscillator();
    const toneGain = audio.createGain();
    tone.type = kind === "angle" ? "sine" : "triangle";
    tone.frequency.setValueAtTime(kind === "angle" ? 820 : 310, now);
    tone.frequency.exponentialRampToValueAtTime(kind === "angle" ? 1120 : 190, now + duration);
    toneGain.gain.setValueAtTime(kind === "angle" ? 0.018 : 0.032, now);
    toneGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    tone.connect(toneGain).connect(audio.destination);
    tone.start(now);
    tone.stop(now + duration + 0.02);
  }
}
